import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FormSelect from "../../../Components/v2/Form/FormSelect";
import FormInput from "../../../Components/v2/Form/FormInput";
import FormRadioGroup from "../../../Components/v2/Form/FormRadioGroup";
import {
  CalculatorCard,
  DetailBox,
  EmptyCalculation,
  FormulaRow,
  LedgerSection,
  NoteBox,
  ResetButton,
  ResultHeadline,
  ResultRow,
} from "../../../Components/Calculator/components";
import {
  PPH_4_2_RATES,
  THRESHOLDS,
  calculateUMKM,
  calculateBungaObligasi,
  calculateBungaKoperasi,
  calculateJasaKonstruksi,
  OBJECT_TYPE_OPTIONS,
  INDIVIDUAL_OPTIONS,
  TAXPAYER_TYPE_OPTIONS,
  CONSTRUCTION_TYPE_OPTIONS,
  QUALIFICATION_OPTIONS,
  SBU_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  OMZET_OPTIONS,
} from "../../../Constants/pphFinal42Rates";
import { PPH_FINAL_42_EXAMPLES } from "../../../Constants/pphFinal42Examples";
import { PPH_FINAL_42_KB_KEYS } from "../../../Constants/pphFinal42KnowledgeBase";
import TaxCalculationExamples from "../../../Components/TaxCalculationExamples";
import KnowledgeBaseSection from "../../../Components/Calculator/KnowledgeBaseSection";
import {
  formatMoney,
  parseCurrencyValue,
  toCamelCase,
} from "../../../Utils/StringUtils";

const CalculatorPphFinal42 = () => {
  const { t } = useTranslation();
  const resultRef = useRef(null);

  const [objectType, setObjectType] = useState(null);
  const [grossAmount, setGrossAmount] = useState("");
  const [isIndividual, setIsIndividual] = useState(true);
  const [omzetRange, setOmzetRange] = useState(null);
  const [taxpayerType, setTaxpayerType] = useState("WPDN");
  const [constructionType, setConstructionType] = useState("PELAKSANA");
  const [qualification, setQualification] = useState("KECIL");
  const [hasSBU, setHasSBU] = useState(true);
  const [propertyType, setPropertyType] = useState("UMUM");
  const [errors, setErrors] = useState({});

  const calculations = useMemo(() => {
    const parsedGross = parseCurrencyValue(grossAmount);

    if (!objectType?.value || parsedGross <= 0) {
      return null;
    }

    let result;
    let note = "";

    switch (objectType.value) {
      case "UMKM": {
        let range = omzetRange
          ? OMZET_OPTIONS.find((opt) => opt.value === omzetRange.value)
          : null;
        let isBelow500 = range && range.max === 500_000_000;
        let isBetween =
          range && range.min === 500_000_000 && range.max === 4_800_000_000;
        let taxable = 0;
        if (isBelow500) {
          taxable = isIndividual ? 0 : parsedGross;
          note = isIndividual
            ? t("taxCalculator.pphFinal42.notes.belowThreshold")
            : t("taxCalculator.pphFinal42.notes.corporateNoExemption");
        } else if (isBetween) {
          taxable = parsedGross;
        } else {
          taxable = calculateUMKM(parsedGross, isIndividual).taxableAmount;
        }
        result = {
          grossAmount: parsedGross,
          taxableAmount: taxable,
          taxAmount: taxable * PPH_4_2_RATES.UMKM,
          rate: PPH_4_2_RATES.UMKM,
        };
        break;
      }

      case "BUNGA_DEPOSITO":
        if (parsedGross < THRESHOLDS.DEPOSITO_MIN_TAXABLE) {
          result = {
            grossAmount: parsedGross,
            taxableAmount: 0,
            taxAmount: 0,
            rate: PPH_4_2_RATES.BUNGA_DEPOSITO,
          };
          note = t("taxCalculator.pphFinal42.notes.belowMinimum");
        } else {
          result = {
            grossAmount: parsedGross,
            taxableAmount: parsedGross,
            taxAmount: parsedGross * PPH_4_2_RATES.BUNGA_DEPOSITO,
            rate: PPH_4_2_RATES.BUNGA_DEPOSITO,
          };
        }
        break;

      case "BUNGA_KOPERASI":
        result = calculateBungaKoperasi(parsedGross);
        result.taxableAmount = result.grossAmount;
        if (result.taxAmount === 0) {
          result.taxableAmount = 0;
          note = t("taxCalculator.pphFinal42.notes.belowThreshold");
        }
        break;

      case "BUNGA_OBLIGASI":
        result = calculateBungaObligasi(parsedGross, taxpayerType);
        result.taxableAmount = result.grossAmount;
        break;

      case "HADIAH_UNDIAN":
        result = {
          grossAmount: parsedGross,
          taxableAmount: parsedGross,
          taxAmount: parsedGross * PPH_4_2_RATES.HADIAH_UNDIAN,
          rate: PPH_4_2_RATES.HADIAH_UNDIAN,
        };
        break;

      case "TRANSAKSI_SAHAM":
        result = {
          grossAmount: parsedGross,
          taxableAmount: parsedGross,
          taxAmount: parsedGross * PPH_4_2_RATES.TRANSAKSI_SAHAM,
          rate: PPH_4_2_RATES.TRANSAKSI_SAHAM,
        };
        break;

      case "SEWA_TANAH_BANGUNAN":
        result = {
          grossAmount: parsedGross,
          taxableAmount: parsedGross,
          taxAmount: parsedGross * PPH_4_2_RATES.SEWA_TANAH_BANGUNAN,
          rate: PPH_4_2_RATES.SEWA_TANAH_BANGUNAN,
        };
        break;

      case "PENGALIHAN_TANAH_BANGUNAN":
        const propertyRate =
          propertyType === "UMUM"
            ? PPH_4_2_RATES.PENGALIHAN_TANAH_BANGUNAN.UMUM
            : PPH_4_2_RATES.PENGALIHAN_TANAH_BANGUNAN.RUMAH_SEDERHANA;
        result = {
          grossAmount: parsedGross,
          taxableAmount: parsedGross,
          taxAmount: parsedGross * propertyRate,
          rate: propertyRate,
        };
        break;

      case "JASA_KONSTRUKSI":
        result = calculateJasaKonstruksi(parsedGross, constructionType, {
          qualification,
          hasSBU,
        });
        result.taxableAmount = result.grossAmount;
        break;

      default:
        return null;
    }

    const netAmount = result.grossAmount - result.taxAmount;

    return {
      objectType: objectType.value,
      grossAmount: result.grossAmount,
      taxableAmount: result.taxableAmount,
      rate: result.rate,
      ratePercentage: result.rate * 100,
      taxAmount: result.taxAmount,
      netAmount,
      note,
    };
  }, [
    objectType,
    grossAmount,
    isIndividual,
    taxpayerType,
    constructionType,
    qualification,
    hasSBU,
    propertyType,
    omzetRange,
    t,
  ]);

  const handleReset = () => {
    setObjectType(null);
    setGrossAmount("");
    setIsIndividual(true);
    setOmzetRange(null);
    setTaxpayerType("WPDN");
    setConstructionType("PELAKSANA");
    setQualification("KECIL");
    setHasSBU(true);
    setPropertyType("UMUM");
    setErrors({});
  };

  const handleObjectTypeChange = (option) => {
    setObjectType(option);
    if (errors.objectType) {
      setErrors((prev) => ({ ...prev, objectType: null }));
    }
  };

  const getGrossAmountMaxAndLength = () => {
    if (objectType?.value === "UMKM" && omzetRange && omzetRange.value) {
      const selected = OMZET_OPTIONS.find(
        (opt) => opt.value === omzetRange.value,
      );
      const max = selected?.max || undefined;
      const maxLength = max ? formatMoney(max).length : 20;
      return { max, maxLength };
    }
    return { max: undefined, maxLength: 20 };
  };

  const handleGrossAmountChange = (e) => {
    let value = e.target.value;
    let numericValue = parseCurrencyValue(value);

    if (max !== undefined && numericValue > max) {
      numericValue = max;
    }

    setGrossAmount(numericValue ? numericValue.toString() : "");
    if (errors.grossAmount) {
      setErrors((prev) => ({ ...prev, grossAmount: null }));
    }
  };

  const { max, maxLength } = getGrossAmountMaxAndLength();

  const handleSelectExample = (exampleData) => {
    const foundObjectType = OBJECT_TYPE_OPTIONS.find(
      (opt) => opt.value === exampleData.objectType,
    );
    setObjectType(foundObjectType || null);
    setGrossAmount(
      exampleData.grossAmount ? formatMoney(exampleData.grossAmount) : "",
    );

    if (exampleData.isIndividual !== undefined) {
      setIsIndividual(exampleData.isIndividual);
    } else {
      setIsIndividual(true);
    }

    if (exampleData.omzetRange) {
      const foundOmzet = OMZET_OPTIONS.find(
        (opt) => opt.value === exampleData.omzetRange,
      );
      setOmzetRange(foundOmzet || null);
    } else {
      setOmzetRange(null);
    }

    if (exampleData.taxpayerType) {
      setTaxpayerType(exampleData.taxpayerType);
    } else {
      setTaxpayerType("WPDN");
    }

    if (exampleData.constructionType) {
      setConstructionType(exampleData.constructionType);
    } else {
      setConstructionType("PELAKSANA");
    }

    if (exampleData.qualification) {
      setQualification(exampleData.qualification);
    } else {
      setQualification("KECIL");
    }

    if (exampleData.hasSBU !== undefined) {
      setHasSBU(exampleData.hasSBU);
    } else {
      setHasSBU(true);
    }

    if (exampleData.propertyType) {
      setPropertyType(exampleData.propertyType);
    } else {
      setPropertyType("UMUM");
    }

    setErrors({});

    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const isValidCalculation = calculations !== null;

  const showIndividualOption = objectType?.value === "UMKM";
  const showOmzetRange = objectType?.value === "UMKM";
  const showTaxpayerType = objectType?.value === "BUNGA_OBLIGASI";
  const showConstructionOptions = objectType?.value === "JASA_KONSTRUKSI";
  const showPropertyType = objectType?.value === "PENGALIHAN_TANAH_BANGUNAN";

  return (
    <div className="space-y-5">
      <TaxCalculationExamples
        examples={PPH_FINAL_42_EXAMPLES}
        onSelectExample={handleSelectExample}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Input Section */}
        <CalculatorCard title={t("taxCalculator.inputData")}>
          <div className="space-y-4">
            <div>
              <FormSelect
                label="taxCalculator.pphFinal42.fields.objectType"
                value={objectType}
                onChange={handleObjectTypeChange}
                options={OBJECT_TYPE_OPTIONS}
                placeholder="taxCalculator.pphFinal42.placeholders.objectType"
                searchable
                isClearable
                error={errors.objectType}
                onClear={() => setObjectType(null)}
              />
            </div>

            {showOmzetRange && (
              <div>
                <FormSelect
                  label="taxCalculator.pphFinal42.fields.accumulatedRevenue"
                  value={omzetRange}
                  onChange={setOmzetRange}
                  options={OMZET_OPTIONS}
                  placeholder="taxCalculator.pphFinal42.placeholders.accumulatedRevenue"
                  isClearable
                />

                <p className="text-xs text-gray-500 mt-1">
                  {t("taxCalculator.pphFinal42.hints.accumulatedOmzet")}
                </p>
              </div>
            )}

            <div>
              <FormInput
                label="taxCalculator.pphFinal42.fields.grossAmount"
                id="grossAmount"
                name="grossAmount"
                value={grossAmount}
                onChange={handleGrossAmountChange}
                placeholder="0"
                numberFormat={{
                  thousandSeparator: ".",
                  decimalSeparator: ",",
                  allowNegative: false,
                  prefix: "Rp ",
                }}
                maxLength={maxLength}
              />
              {errors.grossAmount && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.grossAmount}
                </p>
              )}
            </div>

            {showIndividualOption && (
              <>
                <FormRadioGroup
                  label="taxCalculator.pphFinal42.fields.taxpayerCategory"
                  value={
                    INDIVIDUAL_OPTIONS.find(
                      (opt) => opt.value === isIndividual,
                    ) || INDIVIDUAL_OPTIONS[0]
                  }
                  onChange={(option) => setIsIndividual(option.value)}
                  options={INDIVIDUAL_OPTIONS}
                  gridClassName="grid-cols-2"
                />
                {!isIndividual && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="font-medium mb-1">
                      ⚠️ {t("taxCalculator.pphFinal42.hints.corporateEntity")}
                    </p>
                  </div>
                )}
              </>
            )}

            {showTaxpayerType && (
              <FormRadioGroup
                label="taxCalculator.pphFinal42.fields.taxpayerType"
                value={
                  TAXPAYER_TYPE_OPTIONS.find(
                    (opt) => opt.value === taxpayerType,
                  ) || TAXPAYER_TYPE_OPTIONS[0]
                }
                onChange={(option) => setTaxpayerType(option.value)}
                options={TAXPAYER_TYPE_OPTIONS}
                gridClassName="grid-cols-1"
                verticalSpace="py-2"
              />
            )}

            {showConstructionOptions && (
              <>
                <FormRadioGroup
                  label="taxCalculator.pphFinal42.fields.constructionType"
                  value={
                    CONSTRUCTION_TYPE_OPTIONS.find(
                      (opt) => opt.value === constructionType,
                    ) || CONSTRUCTION_TYPE_OPTIONS[0]
                  }
                  onChange={(option) => setConstructionType(option.value)}
                  options={CONSTRUCTION_TYPE_OPTIONS}
                  gridClassName="grid-cols-1"
                  verticalSpace="py-2"
                />

                {constructionType === "PELAKSANA" && (
                  <FormRadioGroup
                    label="taxCalculator.pphFinal42.fields.qualification"
                    value={
                      QUALIFICATION_OPTIONS.find(
                        (opt) => opt.value === qualification,
                      ) || QUALIFICATION_OPTIONS[0]
                    }
                    onChange={(option) => setQualification(option.value)}
                    options={QUALIFICATION_OPTIONS}
                    gridClassName="grid-cols-2"
                    verticalSpace="py-2"
                  />
                )}

                {(constructionType === "PERENCANA_PENGAWAS" ||
                  constructionType === "TERINTEGRASI") && (
                  <FormRadioGroup
                    label="taxCalculator.pphFinal42.fields.sbu"
                    value={
                      SBU_OPTIONS.find((opt) => opt.value === hasSBU) ||
                      SBU_OPTIONS[0]
                    }
                    onChange={(option) => setHasSBU(option.value)}
                    options={SBU_OPTIONS}
                    gridClassName="grid-cols-2"
                    verticalSpace="py-2"
                  />
                )}
              </>
            )}

            {showPropertyType && (
              <FormRadioGroup
                label="taxCalculator.pphFinal42.fields.propertyType"
                value={
                  PROPERTY_TYPE_OPTIONS.find(
                    (opt) => opt.value === propertyType,
                  ) || PROPERTY_TYPE_OPTIONS[0]
                }
                onChange={(option) => setPropertyType(option.value)}
                options={PROPERTY_TYPE_OPTIONS}
                gridClassName="grid-cols-2"
                verticalSpace="py-2"
              />
            )}
          </div>
        </CalculatorCard>

        {/* Results Section */}
        <div ref={resultRef}>
          <CalculatorCard title={t("taxCalculator.results.title")}>
            {!isValidCalculation ? (
              <EmptyCalculation
                message={t("taxCalculator.pphFinal42.noCalculation")}
              />
            ) : (
              <>
                <ResultHeadline
                  label={t("taxCalculator.pphFinal42.results.pphFinal42")}
                  value={formatMoney(calculations.taxAmount)}
                  caption={t(
                    `taxCalculator.pphFinal42.objectTypes.${toCamelCase(calculations.objectType)}`,
                  )}
                />

                <LedgerSection title={t("taxCalculator.results.taxBasis")}>
                  <ResultRow
                    label={t("taxCalculator.pphFinal42.fields.grossAmount")}
                    value={formatMoney(calculations.grossAmount)}
                  />
                  {calculations.taxableAmount !== calculations.grossAmount && (
                    <ResultRow
                      label={t(
                        "taxCalculator.pphFinal42.results.taxableAmount",
                      )}
                      value={formatMoney(calculations.taxableAmount)}
                    />
                  )}
                  <ResultRow
                    label={t("taxCalculator.pphFinal42.results.rate")}
                    value={`${calculations.ratePercentage.toFixed(2)}%`}
                  />
                </LedgerSection>

                <DetailBox title={t("taxCalculator.calculations.detail")}>
                  <FormulaRow
                    label={t("taxCalculator.pphFinal42.results.formula")}
                    value={`${calculations.ratePercentage.toFixed(2)}% × ${formatMoney(calculations.taxableAmount)}`}
                  />
                  {calculations.note && <NoteBox>{calculations.note}</NoteBox>}
                </DetailBox>

                <ResultRow
                  bold
                  label={t("taxCalculator.pphFinal42.results.netAmount")}
                  value={formatMoney(calculations.netAmount)}
                />

                <ResultRow
                  total
                  label={t("taxCalculator.pphFinal42.results.pphFinal42")}
                  value={formatMoney(calculations.taxAmount)}
                />
              </>
            )}
          </CalculatorCard>
        </div>
      </div>

      <ResetButton onClick={handleReset} />

      {/* Knowledge Base / Educational SEO Footer */}
      <KnowledgeBaseSection keys={PPH_FINAL_42_KB_KEYS} />
    </div>
  );
};

export default CalculatorPphFinal42;
